import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import type { NotificationItemViewModel } from '@/features/activity/ActivityView/ActivityViewModel'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { links } from '@/utils/link'
import { useDocumentTitle } from '@/hooks/document-title'
import { NotificationPreferencesEditorDialogViewContainer } from '@/features/activity/NotificationPreferencesEditorDialogView/NotificationPreferencesEditorDialogViewContainer'
import { dateFormatters } from '@/utils/date-util'
import {
  ActivityView,
  ActivityViewContent,
  NotificationItem,
  NotificationItemSkeleton,
  NotificationsSection,
  NotificationsSectionSkeleton,
  NotificationsEmptyState,
} from './'
import { Avatar } from '@/components/Avatar'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'

export default observer(function ActivityViewContainer() {
  useDocumentTitle('Activity')

  const {
    internal: { activityVm, preferencesVm },
  } = useFeatures((rootStore) => ({
    internal: rootStore.activity.internal(),
  }))

  useActivation(activityVm)

  return (
    <>
      <ActivityView
        isEmpty={activityVm.notifications.empty}
        hasRecentActivity={activityVm.notifications.recent.length > 0}
        onMarkAsReadPress={activityVm.markAllRead}
        isMarkAsReadPending={activityVm.markAllRead.pending}
      >
        <ActivityViewContent>
          {activityVm.notifications.recent.length > 0 && (
            <NotificationsSection title="Recent activity">
              {activityVm.notifications.recent.map((vm) => (
                <NotificationItemContainer vm={vm} key={vm.notification.id} />
              ))}
            </NotificationsSection>
          )}

          {activityVm.notifications.past.length > 0 && (
            <NotificationsSection title="Past activity" isPastActivity>
              {activityVm.notifications.past.map((vm) => (
                <NotificationItemContainer vm={vm} key={vm.notification.id} />
              ))}
            </NotificationsSection>
          )}

          {activityVm.notifications.empty && activityVm.scroller.isDone && (
            <NotificationsEmptyState />
          )}

          {activityVm.notifications.empty &&
          activityVm.scroller.fetch.pending ? (
            <NotificationsSectionSkeleton title="Placeholder">
              <SkeletonListInfiniteScrollTrigger
                maxSkeletons={10}
                scroller={activityVm.scroller}
              >
                <NotificationItemSkeleton />
              </SkeletonListInfiniteScrollTrigger>
            </NotificationsSectionSkeleton>
          ) : (
            <SkeletonListInfiniteScrollTrigger scroller={activityVm.scroller}>
              <NotificationItemSkeleton />
            </SkeletonListInfiniteScrollTrigger>
          )}
        </ActivityViewContent>
      </ActivityView>
      <NotificationPreferencesEditorDialogViewContainer vm={preferencesVm} />
    </>
  )
})

interface NotificationItemContainerProps {
  vm: NotificationItemViewModel
}

export const NotificationItemContainer = observer(
  function NotificationItemContainer({ vm }: NotificationItemContainerProps) {
    const href =
      vm.notification.action.type === 'ViewJob'
        ? links.jobDetails(vm.notification.action.jobId)
        : undefined

    return (
      <NotificationItem
        textValue={`${vm.plainText} on ${dateFormatters.dateTimeDefault.format(vm.notification.dispatchedAt)}`}
        to={href}
        onAction={vm.markRead}
        text={vm.parsedText}
        dispatchedAt={vm.notification.dispatchedAt}
        avatar={<NotificationImageContainer vm={vm} />}
      />
    )
  },
)

export const NotificationImageContainer = observer(
  function NotificationImageContainer({ vm }: NotificationItemContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    const lightDark = useLightDarkFn()

    switch (vm.notification.image.type) {
      case 'Static':
        return (
          <Avatar
            size={48}
            subject={{
              displayName: '',
              avatarUrl: vm.notification.image.url,
            }}
          />
        )
      case 'UserAvatar':
        return (
          <Avatar
            size={48}
            subject={{
              displayName: vm.notification.image.user.displayName,
              avatarUrl: vm.notification.image.user.avatarUrl,
            }}
          />
        )
      case 'None':
        return (
          <Avatar
            size={48}
            subject={{
              displayName: shellInfo.name,
              avatarUrl: lightDark(
                shellInfo.iconUrl,
                shellInfo.invertedIconUrl,
              ),
            }}
          />
        )
    }
  },
)
